import React, { useMemo, useState } from "react";
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

/**
 * SkillsUprise Admin - Users Analytics Section
 * - KPI cards
 * - Trends (Signups + Active users)
 * - Status distribution (Pie)
 * - Status over time (Stacked bars)
 * - Better users table with search + status filter + date filter (basic)
 *
 * Replace MOCK_USERS + MOCK_DAILY with your API data.
 */

const MOCK_USERS = [
  {
    id: 17687,
    name: "Junugari Praveen",
    email: "jjunugaripraveen@gmail.com",
    phone: "6305371509",
    joinedAt: "2026-02-03T00:10:42",
    status: "ACTIVE",
    lastSeenAt: "2026-02-03T00:30:11",
    plan: "Pro",
    source: "Instagram",
  },
  {
    id: 17686,
    name: "Sai Kumar",
    email: "saikatepalli532@gmail.com",
    phone: "7981989314",
    joinedAt: "2026-02-02T21:04:54",
    status: "ACTIVE",
    lastSeenAt: "2026-02-03T09:10:02",
    plan: "Basic",
    source: "Referral",
  },
  {
    id: 17685,
    name: "Sri Lakshmi",
    email: "srichowdary1728@gmail.com",
    phone: "7330966020",
    joinedAt: "2026-02-02T18:58:18",
    status: "PENDING",
    lastSeenAt: null,
    plan: "Trial",
    source: "Website",
  },
  {
    id: 17684,
    name: "Gundlaplly Vaishnavi",
    email: "gundlapallyvaishnavi19@gmail.com",
    phone: "9059466194",
    joinedAt: "2026-02-02T17:40:40",
    status: "ACTIVE",
    lastSeenAt: "2026-02-02T20:10:10",
    plan: "Pro",
    source: "YouTube",
  },
];

const MOCK_DAILY = [
  { date: "Jan 28", signups: 18, active: 2400, pending: 34, inactive: 12 },
  { date: "Jan 29", signups: 22, active: 2420, pending: 28, inactive: 14 },
  { date: "Jan 30", signups: 15, active: 2450, pending: 26, inactive: 11 },
  { date: "Jan 31", signups: 28, active: 2480, pending: 21, inactive: 9 },
  { date: "Feb 01", signups: 40, active: 2550, pending: 18, inactive: 8 },
  { date: "Feb 02", signups: 36, active: 2620, pending: 16, inactive: 7 },
  { date: "Feb 03", signups: 44, active: 2685, pending: 14, inactive: 6 },
];

const STATUS_COLORS = {
  ACTIVE: "#10b981",
  PENDING: "#f59e0b",
  INACTIVE: "#ef4444",
};

function fmtInt(n) {
  return new Intl.NumberFormat("en-IN").format(Number(n || 0));
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { hour12: true });
}

function parseISODate(iso) {
  // returns yyyy-mm-dd
  if (!iso) return "";
  return iso.slice(0, 10);
}

function Badge({ status }) {
  const base =
    "inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold";
  const map = {
    ACTIVE: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
    PENDING: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
    INACTIVE: "bg-rose-50 text-rose-700 ring-1 ring-rose-200",
  };
  return (
    <span className={`${base} ${map[status] || "bg-slate-50 text-slate-700"}`}>
      <span
        className="h-2 w-2 rounded-full"
        style={{ background: STATUS_COLORS[status] || "#64748b" }}
      />
      {status}
    </span>
  );
}

function KPI({ title, value, sub, trend, icon }) {
  return (
    <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-100">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold tracking-wide text-slate-500">
            {title}
          </p>
          <p className="mt-2 text-2xl font-bold text-slate-900">{value}</p>
          {sub ? (
            <p className="mt-1 text-xs text-slate-500">
              <span className="font-semibold text-slate-700">{sub}</span>
            </p>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-slate-50 ring-1 ring-slate-100">
            <span className="text-slate-700">{icon}</span>
          </div>
          {trend ? (
            <span
              className={`rounded-full px-2 py-1 text-xs font-semibold ${
                trend.startsWith("+")
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-rose-50 text-rose-700"
              }`}
            >
              {trend}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function SU_Tooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl bg-white p-3 shadow-lg ring-1 ring-slate-200">
      <p className="text-xs font-semibold text-slate-700">{label}</p>
      <div className="mt-2 space-y-1">
        {payload.map((p) => (
          <div key={p.dataKey} className="flex items-center justify-between gap-6">
            <span className="text-xs text-slate-600">{p.name}</span>
            <span className="text-xs font-semibold text-slate-900">
              {fmtInt(p.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function UsersAnalyticsSection() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");
  const [date, setDate] = useState(""); // yyyy-mm-dd

  // Replace with your API users list
  const users = MOCK_USERS;

  const stats = useMemo(() => {
    const total = users.length;
    const active = users.filter((u) => u.status === "ACTIVE").length;
    const pending = users.filter((u) => u.status === "PENDING").length;
    const inactive = users.filter((u) => u.status === "INACTIVE").length;

    // Example metrics (you can compute from real data)
    const last7Signups = MOCK_DAILY.reduce((s, d) => s + d.signups, 0);
    const conversionRate = total ? ((active / total) * 100).toFixed(1) : "0.0";

    return {
      total,
      active,
      pending,
      inactive,
      last7Signups,
      conversionRate,
    };
  }, [users]);

  const statusPie = useMemo(() => {
    return [
      { name: "ACTIVE", value: stats.active },
      { name: "PENDING", value: stats.pending },
      { name: "INACTIVE", value: stats.inactive },
    ].filter((x) => x.value > 0);
  }, [stats]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return users.filter((u) => {
      const matchesQ =
        !needle ||
        u.name.toLowerCase().includes(needle) ||
        u.email.toLowerCase().includes(needle) ||
        String(u.id).includes(needle) ||
        (u.phone || "").includes(needle);

      const matchesStatus = status === "ALL" ? true : u.status === status;

      const matchesDate = date ? parseISODate(u.joinedAt) === date : true;

      return matchesQ && matchesStatus && matchesDate;
    });
  }, [users, q, status, date]);

  return (
    <div className="w-full bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Users Analytics</h2>
            <p className="text-sm text-slate-500">
              Track growth, activity, and onboarding status in one place.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700">
              + Add User
            </button>
            <button className="rounded-xl bg-white px-4 py-2 text-sm font-semibold text-slate-800 ring-1 ring-slate-200 hover:bg-slate-50">
              Import
            </button>
            <button className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800">
              Export
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <KPI
            title="TOTAL USERS"
            value={fmtInt(stats.total)}
            sub={`Last 7 days signups: ${fmtInt(stats.last7Signups)}`}
            trend="+4.8%"
            icon="👥"
          />
          <KPI
            title="ACTIVE USERS"
            value={fmtInt(stats.active)}
            sub={`Conversion: ${stats.conversionRate}%`}
            trend="+3.1%"
            icon="✅"
          />
          <KPI
            title="PENDING ONBOARDING"
            value={fmtInt(stats.pending)}
            sub="Verify email / payment pending"
            trend="-1.2%"
            icon="⏳"
          />
          <KPI
            title="INACTIVE USERS"
            value={fmtInt(stats.inactive)}
            sub="No activity in 30 days"
            trend="-0.6%"
            icon="⚠️"
          />
        </div>

        {/* Charts */}
        <div className="mt-6 grid gap-4 xl:grid-cols-3">
          {/* Growth */}
          <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-100 xl:col-span-2">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-bold text-slate-900">
                  Growth & Engagement (7 days)
                </p>
                <p className="text-xs text-slate-500">
                  Signups trend + Active user base
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                  Healthy
                </span>
              </div>
            </div>

            <div className="mt-4 h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={MOCK_DAILY} margin={{ left: 0, right: 12 }}>
                  <defs>
                    <linearGradient id="gradSignups" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopOpacity={0.25} />
                      <stop offset="100%" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip content={<SU_Tooltip />} />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="signups"
                    name="Signups"
                    stroke="#0ea5e9"
                    fill="url(#gradSignups)"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="active"
                    name="Active Users"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Status Distribution */}
          <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-100">
            <p className="text-sm font-bold text-slate-900">User Status Mix</p>
            <p className="text-xs text-slate-500">
              Quick snapshot of onboarding + retention
            </p>

            <div className="mt-4 h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Tooltip content={<SU_Tooltip />} />
                  <Legend />
                  <Pie
                    data={statusPie}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={55}
                    outerRadius={90}
                    paddingAngle={4}
                  >
                    {statusPie.map((entry) => (
                      <Cell
                        key={entry.name}
                        fill={STATUS_COLORS[entry.name] || "#64748b"}
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-2 grid grid-cols-3 gap-2 text-center">
              <div className="rounded-xl bg-emerald-50 p-2">
                <p className="text-xs text-emerald-700">Active</p>
                <p className="text-sm font-bold text-emerald-900">{fmtInt(stats.active)}</p>
              </div>
              <div className="rounded-xl bg-amber-50 p-2">
                <p className="text-xs text-amber-700">Pending</p>
                <p className="text-sm font-bold text-amber-900">{fmtInt(stats.pending)}</p>
              </div>
              <div className="rounded-xl bg-rose-50 p-2">
                <p className="text-xs text-rose-700">Inactive</p>
                <p className="text-sm font-bold text-rose-900">{fmtInt(stats.inactive)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stacked Bar: Status over time */}
        <div className="mt-6 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-100">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-bold text-slate-900">Onboarding Funnel</p>
              <p className="text-xs text-slate-500">
                Pending vs inactive movement across recent days
              </p>
            </div>
            <button className="rounded-xl bg-white px-3 py-2 text-xs font-semibold text-slate-800 ring-1 ring-slate-200 hover:bg-slate-50">
              View Details
            </button>
          </div>

          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={MOCK_DAILY} margin={{ right: 12 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip content={<SU_Tooltip />} />
                <Legend />
                <Bar dataKey="pending" name="Pending" stackId="a" fill="#f59e0b" radius={[10, 10, 0, 0]} />
                <Bar dataKey="inactive" name="Inactive" stackId="a" fill="#ef4444" radius={[10, 10, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Filters + Table */}
        <div className="mt-6 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-100">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-bold text-slate-900">Users</p>
            <div className="text-xs text-slate-500">
              Showing <span className="font-semibold text-slate-700">{filtered.length}</span> users
            </div>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-4">
            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-slate-600">Search</label>
              <div className="mt-1 flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2 ring-1 ring-slate-200">
                <span className="text-slate-400">🔎</span>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search name, email, phone or user id"
                  className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600">Joined Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="mt-1 w-full rounded-xl bg-slate-50 px-3 py-2 text-sm ring-1 ring-slate-200 outline-none"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="mt-1 w-full rounded-xl bg-slate-50 px-3 py-2 text-sm ring-1 ring-slate-200 outline-none"
              >
                <option value="ALL">All</option>
                <option value="ACTIVE">Active</option>
                <option value="PENDING">Pending</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>
          </div>

          <div className="mt-4 overflow-x-auto rounded-2xl ring-1 ring-slate-100">
            <table className="min-w-full bg-white">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs font-semibold text-slate-600">
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Joined</th>
                  <th className="px-4 py-3">Last Seen</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Action</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((u) => (
                  <tr
                    key={u.id}
                    className="border-t border-slate-100 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="grid h-9 w-9 place-items-center rounded-full bg-slate-100 text-slate-700">
                          {u.name?.trim()?.[0]?.toUpperCase() || "U"}
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900">{u.name}</div>
                          <div className="text-xs text-slate-500">User ID: {u.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">{u.email}</td>
                    <td className="px-4 py-3">{u.phone}</td>
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                        {u.plan || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3">{u.source || "—"}</td>
                    <td className="px-4 py-3">{fmtDateTime(u.joinedAt)}</td>
                    <td className="px-4 py-3">{fmtDateTime(u.lastSeenAt)}</td>
                    <td className="px-4 py-3">
                      <Badge status={u.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button className="rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 ring-1 ring-slate-200 hover:bg-slate-50">
                          View
                        </button>
                        <button className="rounded-lg bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 ring-1 ring-rose-200 hover:bg-rose-100">
                          Delete
                        </button>
                        <button className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800">
                          ⋮
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-10 text-center text-sm text-slate-500">
                      No users found for current filters.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          {/* footer actions */}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-slate-500">
              Tip: connect charts to your real metrics (daily signups, DAU, churn, pending).
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setQ("");
                  setStatus("ALL");
                  setDate("");
                }}
                className="rounded-xl bg-white px-4 py-2 text-sm font-semibold text-slate-800 ring-1 ring-slate-200 hover:bg-slate-50"
              >
                Reset Filters
              </button>
              <button className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
